import Cookies from "js-cookie";
import { useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { PRACTICE_SCENARIOS, PracticeScenario, setPracticeScenario } from "@utils/practice/practiceMode";
import { needsIdentity, seedPracticeIdentity } from "@utils/practice/practiceIdentity";
import { resetPracticeOrder, resetPracticeSurvey, seedGasShopOrder } from "@utils/practice/practiceOrder";

type NavigateFn = (to: string, opts?: { replace?: boolean }) => void;

/**
 * 啟動（或重新啟動）一個練習情境：
 * 1. 設定情境旗標
 * 2. 清掉前一輪的暫存狀態（瓦斯行資料、序號 cookie/session、家庭邀請碼）
 * 3. 視情境設定家庭邀請碼
 * 4. 導向該情境的起始畫面
 */
export const startScenario = (scenario: PracticeScenario, navigate: NavigateFn) => {
  setPracticeScenario(scenario);

  // 清除上一輪殘留，確保每次練習都是乾淨起點。
  useUser.getState().setInvGasData(null);
  Cookies.remove("invite");
  sessionStorage.removeItem("invite");
  sessionStorage.removeItem("family_invite_code");
  // gasShop「自動帶入上次訂單」只做一次的旗標：重啟情境時清掉，讓每次練習都會自動帶入一筆，
  // 避免購物車空被踢回商品頁。
  sessionStorage.removeItem("gasShop_autoPopulatedFromLastOrder");
  useOrder.getState().clearProducts();
  resetPracticeOrder();
  // 用氣習慣問卷：清掉上一輪填過的本地紀錄，讓新客戶情境每次都會在首頁看到問卷卡。
  resetPracticeSurvey();
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("gas_survey_v1:"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* 私密模式等讀寫失敗時忽略 */
  }

  // 重置 kiosk 首次設定相關旗標（避免上一輪殘留）；firstJoinSetup 情境才開 needIdentityCheck。
  const kiosk = useKiosk.getState();
  kiosk.setPendingReview(false);
  kiosk.setContractSigned(false);
  kiosk.setNotFound(false);
  kiosk.setNeedIdentityCheck(scenario === "firstJoinSetup");
  // 每次重新開始情境都要求首次結帳的簡訊 OTP（上一輪驗過會殘留 otpVerified=true，導致結算頁不再出現驗證碼）。
  kiosk.setOtpVerified(false);

  // 需要既有身分的情境：seed 身分/瓦斯行/地址，讓頁面能直接運作；其餘（綁定/掃碼）清掉身分避免殘留。
  if (needsIdentity(scenario)) {
    seedPracticeIdentity();
  } else {
    useUser.getState().setChooseIdentity(null);
  }

  // 瓦斯訂購：預載一筆商品到購物車（等同自動帶入上次訂單），確保到購物車頁不會因空而被踢回。
  if (scenario === "orderGasShop") {
    useOrder.getState().syncDraft(seedGasShopOrder());
  }

  const meta = PRACTICE_SCENARIOS.find((s) => s.key === scenario);
  navigate(meta?.start || "/register/step2/inviteCode", { replace: true });
};
